import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Building2, Users, Home, TrendingUp, Activity, RefreshCw, AlertCircle } from 'lucide-react'
import { useDashboardData } from '@/hooks/useDashboardData'

export function DashboardPage() {
  const { stats, isLoading, error, refetch } = useDashboardData()

  const activeTenantRate = stats.total_tenants > 0
    ? Math.round((stats.active_tenants / stats.total_tenants) * 100)
    : null

  const kpiCards = [
    {
      title: 'Total de Tenants',
      value: stats.total_tenants,
      icon: Building2,
      color: 'text-brand-purple-600',
      bgColor: 'bg-brand-purple-100'
    },
    {
      title: 'Usuários Ativos',
      value: stats.total_users.toLocaleString(),
      icon: Users,
      color: 'text-brand-gold-600',
      bgColor: 'bg-brand-gold-100'
    },
    {
      title: 'Propriedades',
      value: stats.total_properties.toLocaleString(),
      icon: Home,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100'
    },
    {
      title: 'Crescimento',
      value: `${stats.growth_rate > 0 ? '+' : ''}${stats.growth_rate}%`,
      icon: TrendingUp,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    }
  ]

  // Tratamento de erro
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Erro ao Carregar Dados
            </h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={refetch} className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Tentar Novamente
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-8 animate-pulse">
        <div className="space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900">
            Dashboard Executivo
          </h1>
          <Badge 
            variant={activeTenantRate === null || activeTenantRate > 70 ? 'default' : 'destructive'}
            className="flex items-center gap-2"
          >
            <Activity className="h-3 w-3" />
            {activeTenantRate === null ? 'Sem tenants' : `${activeTenantRate}% tenants ativos`}
          </Badge>
        </div>
        <p className="text-lg text-muted-foreground">
          Visão geral da plataforma SaaS QuadraDois e indicadores executivos
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiCards.map((card, index) => {
          const Icon = card.icon
          return (
            <Card key={index} className="relative overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-muted-foreground">
                      {card.title}
                    </p>
                    <p className="text-3xl font-bold tracking-tight">
                      {card.value}
                    </p>
                  </div>
                  <div className={`p-3 rounded-full ${card.bgColor}`}>
                    <Icon className={`h-6 w-6 ${card.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
